const SHORTEN_TIMEOUT_MS = 4_000;
const CACHE_LIMIT = 12;

type FetchLike = typeof fetch;

function cacheKey(beatHash: string, room?: string): string {
  return `${beatHash}\n${room ?? ''}`;
}

/**
 * Session-local short-link cache. `prime()` is deliberately decoupled from the
 * click handler so Web Share / Clipboard can be invoked without awaiting a
 * network round trip and losing transient user activation.
 */
export class ShortLinkCache {
  private cache = new Map<string, string>();
  private pending = new Map<string, Promise<string | null>>();

  constructor(
    private fetcher: FetchLike = fetch,
    private origin: string = window.location.origin,
    private timeoutMs = SHORTEN_TIMEOUT_MS,
  ) {}

  get(beatHash: string, room?: string): string | null {
    return this.cache.get(cacheKey(beatHash, room)) ?? null;
  }

  prime(beatHash: string, room?: string): Promise<string | null> {
    const key = cacheKey(beatHash, room);
    const cached = this.cache.get(key);
    if (cached) return Promise.resolve(cached);
    const inFlight = this.pending.get(key);
    if (inFlight) return inFlight;

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), this.timeoutMs);
    const request = this.fetcher('/api/shorten', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(room ? { beatHash, room } : { beatHash }),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return null;
      const data = (await response.json()) as { shortId?: unknown };
      const id = typeof data.shortId === 'string' ? data.shortId : '';
      if (!/^[a-z0-9]{4,16}$/.test(id)) return null;
      const url = `${this.origin}/s/${id}`;
      this.cache.set(key, url);
      while (this.cache.size > CACHE_LIMIT) {
        const oldest = this.cache.keys().next().value as string | undefined;
        if (oldest === undefined) break;
        this.cache.delete(oldest);
      }
      return url;
    }).catch(() => null).finally(() => {
      window.clearTimeout(timer);
      this.pending.delete(key);
    });

    this.pending.set(key, request);
    return request;
  }
}

function legacyCopy(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.readOnly = true;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }
  textarea.remove();
  return copied;
}

/**
 * Starts the clipboard operation before returning, preserving the caller's
 * transient activation. Older browsers get a synchronous execCommand fallback.
 */
export function copyTextNow(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      return navigator.clipboard.writeText(text).then(() => true, () => false);
    } catch {
      return Promise.resolve(false);
    }
  }
  return Promise.resolve(legacyCopy(text));
}
